import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router';

import { Text, Heading } from '@zillow/constellation';
import { Col } from '@zillow/zrs-comet';

import { GA_EVENTS_INTERESTED_RENTERS_MODULE } from 'app/shared/constants/AnalyticsConstants';
import { RMWContext } from 'app/shared/providers/RMWContext';
import { RoutingSelectors } from 'app/shared/selectors';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import LeadActions from 'app/shared/actions/LeadActions';

import {
    StyledCard,
    StyledColHiddenOnXs,
    StyledIconChevronRightCircle,
    StyledLabel,
    StyledRow,
} from './StyledComponents';

const propTypes = {
    updateMinModuleHeight: PropTypes.func,
};

const defaultProps = {
    updateMinModuleHeight: () => {},
};

const CARD_LABELS = {
    NEW: ' New',
    NOT_REPLIED_TO: ' Not replied to',
    TOTAL: ' Total',
};

const {
    NEW_INQUIRIES_CLICK,
    UN_REPLIED_INQUIRIES_CLICK,
    TOTAL_INQUIRIES_CLICK,
} = GA_EVENTS_INTERESTED_RENTERS_MODULE;

const ConnectedInquiries = ({ updateMinModuleHeight }) => {
    const { paths } = useContext(RMWContext);
    const dispatch = useDispatch();
    const listingId = useSelector(state => RoutingSelectors.makeListingId(state));

    const [inquiryCounts, setInquiryCounts] = useState({
        unreadCount: 0,
        notRepliedToCount: 0,
        totalConversationsCount: 0,
    });

    useEffect(() => {
        dispatch(LeadActions.getConversationCountsForListing({ listingAlias: listingId }))
            .then((res) => {
                const {
                    unreadCount,
                    notRepliedToCount,
                    totalConversationsCount,
                } = res;

                setInquiryCounts({
                    unreadCount,
                    notRepliedToCount,
                    totalConversationsCount,
                });
            }).finally(() => {
                updateMinModuleHeight();
            });
    }, [dispatch, listingId]);

    const {
        unreadCount,
        notRepliedToCount,
        totalConversationsCount,
    } = inquiryCounts;

    const triggerGAEvent = (label, value) => {
        let gaEvent = {};

        switch (label) {
            case CARD_LABELS.NEW:
                gaEvent = NEW_INQUIRIES_CLICK;
                break;
            case CARD_LABELS.NOT_REPLIED_TO:
                gaEvent = UN_REPLIED_INQUIRIES_CLICK;
                break;
            case CARD_LABELS.TOTAL:
                gaEvent = TOTAL_INQUIRIES_CLICK;
                break;
            default:
                console.warn('No case for', label);
                break;
        }

        dispatch(analyticsUtils.track.GAEventV2({
            ...gaEvent,
            value,
        }));

        return false;
    };

    const handleCardClick = (filter = '', label = '', count = 0) => {
        // always filter with byProperty
        const queryParams = filter ? `?${filter}=true&byProperty=true` : '?byProperty=true';

        triggerGAEvent(label, count);
        window.router.updateLocation(`${paths.base}/inbox/${listingId}${queryParams}`);
    };

    return (
        <div className="ConnectedInquiries">
            <Heading level={5} paddingBottom="sm">Inquiries</Heading>
            <StyledRow>
                <Col xs={6} lg={12}>
                    <StyledCard
                        onClick={() => handleCardClick(
                            'unreadOnly',
                            CARD_LABELS.NEW,
                            unreadCount
                        )}
                        cardType="secondary"
                        marginRight="xs"
                    >
                        <StyledLabel>
                            <Text data-test="unread-value" fontType="bodySmallHeading">{unreadCount}</Text>
                            <Text fontType="bodySmall" data-test="unread-text">{CARD_LABELS.NEW}</Text>
                        </StyledLabel>
                        <StyledIconChevronRightCircle size="sm" />
                    </StyledCard>
                </Col>
                <StyledColHiddenOnXs xs={6} lg={12}>
                    <StyledCard
                        onClick={() => handleCardClick(
                            'notRepliedToOnly',
                            CARD_LABELS.NOT_REPLIED_TO,
                            notRepliedToCount
                        )}
                        cardType="secondary"
                        marginLeft="xs"
                    >
                        <StyledLabel>
                            <Text data-test="notRepliedTo-value" fontType="bodySmallHeading">{notRepliedToCount}</Text>
                            <Text data-test="notRepliedTo-text" fontType="bodySmall">{CARD_LABELS.NOT_REPLIED_TO}</Text>
                        </StyledLabel>
                        <StyledIconChevronRightCircle size="sm" />
                    </StyledCard>
                </StyledColHiddenOnXs>
                <Col xs={6} lg={12}>
                    <StyledCard
                        onClick={() => handleCardClick(
                            '',
                            CARD_LABELS.TOTAL,
                            totalConversationsCount
                        )}
                        cardType="secondary"
                    >
                        <StyledLabel>
                            <Text data-test="total-value" fontType="bodySmallHeading">{totalConversationsCount}</Text>
                            <Text data-test="total-text" fontType="bodySmall">{CARD_LABELS.TOTAL}</Text>
                        </StyledLabel>
                        <StyledIconChevronRightCircle size="sm" />
                    </StyledCard>
                </Col>
            </StyledRow>
        </div>
    );
};

ConnectedInquiries.propTypes = propTypes;
ConnectedInquiries.defaultProps = defaultProps;

export { ConnectedInquiries };
export default withRouter(ConnectedInquiries);
